<template>
  <!-- Режим встраивания: только карточки загрузки и списка (без страницы, заголовка и блока «Место на сервере») -->
  <div v-if="embedMode" class="admin-video-embed">
    <p v-if="!lockedSectionKey" class="empty-hint embed-pick-hint">Выберите подраздел, чтобы загрузить или просмотреть видео.</p>
    <template v-else>
    <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Загрузить видео в «{{ lockedSectionTitle }}»</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-item>
            <ion-input label="Название для пользователей" label-placement="stacked" v-model="uploadDisplayName"
              placeholder="Отображаемое название видео"></ion-input>
          </ion-item>
          <input type="file" ref="fileInput" accept="video/*" class="file-input-hidden" @change="onFileSelected" />
          <div
            class="drop-zone"
            :class="{ 'drop-zone--active': videoDragOver, 'drop-zone--has-file': !!selectedFile, 'drop-zone--disabled': uploadInProgress }"
            @dragenter.prevent="onVideoDragEnter"
            @dragover.prevent="onVideoDragOver"
            @dragleave.prevent="onVideoDragLeave"
            @drop.prevent="onVideoDrop"
            @click="pickVideoFile()"
          >
            <ion-icon class="drop-zone-icon" :icon="cloudUploadOutline"></ion-icon>
            <p class="drop-zone-title">{{ selectedFile ? selectedFile.name : 'Перетащите видео сюда' }}</p>
            <p class="drop-zone-hint">
              <template v-if="selectedFile">
                {{ formatBytes(selectedFile.size) }}
                <span v-if="diskSpace && selectedFile.size > diskSpace.availableBytes" class="file-too-big"> · недостаточно места</span>
                <span v-else-if="selectedFile.size > LARGE_FILE_WARN_BYTES"> · чанковая загрузка</span>
                <span v-else> · нажмите, чтобы заменить</span>
              </template>
              <template v-else>или нажмите, чтобы выбрать файл</template>
            </p>
          </div>
          <ion-progress-bar v-if="uploadInProgress" :value="uploadProgressPercent / 100" type="determinate"></ion-progress-bar>
          <p v-if="uploadInProgress" class="upload-progress-text">{{ uploadProgressPercent }}%</p>
          <p v-if="uploadInProgress" class="upload-warning">⚠️ Не закрывайте окно до завершения загрузки.</p>
          <ion-button expand="block" @click="doUpload()" :disabled="!canUpload || uploadInProgress">
            {{ uploadInProgress ? 'Загрузка…' : 'Загрузить на сервер' }}
          </ion-button>
        </ion-card-content>
      </ion-card>
      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Видео «{{ lockedSectionTitle }}»</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p v-if="hasProcessingVideos" class="info-hint">ℹ️ Конвертация видео идёт на сервере. Можно закрыть окно и вернуться позднее — прогресс обновится автоматически.</p>
          <div v-if="videos.length" class="media-file-list">
            <div v-for="v in videos" :key="v.id" class="media-file-row">
              <div class="media-file-main">
                <div class="media-file-title">{{ v.displayName }}</div>
                <div class="media-file-meta">{{ v.sourceType }}</div>
                <ion-progress-bar
                  v-if="isProcessingWithProgress(v)"
                  :value="(conversionProgress[Number(v.id)]?.percent ?? 0) / 100"
                  type="determinate"
                  class="conversion-progress"
                ></ion-progress-bar>
              </div>
              <span class="media-status" :class="'media-status--' + effectiveStatus(v)">{{ effectiveStatusLabel(v) }}</span>
              <div class="media-file-actions">
                <button type="button" class="media-icon-btn" @click="renameVideo(v)" title="Переименовать">
                  <ion-icon :icon="pencilOutline"></ion-icon>
                </button>
                <button
                  v-if="v.status === 'PROCESSING'"
                  type="button"
                  class="media-icon-btn"
                  @click="cancelConversion(v)"
                  title="Остановить"
                >
                  <ion-icon :icon="stopCircleOutline"></ion-icon>
                </button>
                <button
                  v-if="v.status === 'FAILED' || v.status === 'CANCELLED'"
                  type="button"
                  class="media-icon-btn"
                  @click="retryConversion(v)"
                  title="Повторить"
                >
                  <ion-icon :icon="refreshOutline"></ion-icon>
                </button>
                <button
                  v-if="v.status === 'READY' || v.status === 'FAILED' || v.status === 'CANCELLED'"
                  type="button"
                  class="media-icon-btn media-icon-btn--danger"
                  @click="deleteVideo(v)"
                  title="Удалить"
                >
                  <ion-icon :icon="trashOutline"></ion-icon>
                </button>
              </div>
            </div>
          </div>
          <p v-else class="empty-hint">Нет видео в этом подразделе.</p>
        </ion-card-content>
      </ion-card>
    </template>
  </div>
  <ion-page v-else>
    <PageHeaderWithMenu title="Загрузка видео" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-card class="admin-card disk-card">
        <ion-card-header>
          <ion-card-title>Место на сервере</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p v-if="diskSpace">Свободно: {{ formatBytes(diskSpace.availableBytes) }} из {{ formatBytes(diskSpace.totalBytes) }}</p>
          <p v-else class="ion-text-wrap">Загрузка данных о месте…</p>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Загрузить видео</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-item>
            <ion-select label="Подраздел медиатеки" label-placement="stacked" v-model="uploadSectionKey"
              interface="popover" mode="ios" placeholder="Выберите раздел">
              <ion-select-option v-for="s in educationSections" :key="s.id || s.folderKey" :value="s.folderKey">{{ s.name }} ({{ s.folderKey }})</ion-select-option>
            </ion-select>
          </ion-item>
          <ion-item>
            <ion-input label="Название для пользователей" label-placement="stacked" v-model="uploadDisplayName"
              placeholder="Отображаемое название видео"></ion-input>
          </ion-item>
          <input type="file" ref="fileInput" accept="video/*" class="file-input-hidden" @change="onFileSelected" />
          <div
            class="drop-zone"
            :class="{ 'drop-zone--active': videoDragOver, 'drop-zone--has-file': !!selectedFile, 'drop-zone--disabled': uploadInProgress }"
            @dragenter.prevent="onVideoDragEnter"
            @dragover.prevent="onVideoDragOver"
            @dragleave.prevent="onVideoDragLeave"
            @drop.prevent="onVideoDrop"
            @click="pickVideoFile()"
          >
            <ion-icon class="drop-zone-icon" :icon="cloudUploadOutline"></ion-icon>
            <p class="drop-zone-title">{{ selectedFile ? selectedFile.name : 'Перетащите видео сюда' }}</p>
            <p class="drop-zone-hint">
              <template v-if="selectedFile">
                {{ formatBytes(selectedFile.size) }}
                <span v-if="diskSpace && selectedFile.size > diskSpace.availableBytes" class="file-too-big"> · недостаточно места</span>
                <span v-else-if="selectedFile.size > LARGE_FILE_WARN_BYTES"> · чанковая загрузка</span>
                <span v-else> · нажмите, чтобы заменить</span>
              </template>
              <template v-else>или нажмите, чтобы выбрать файл</template>
            </p>
          </div>
          <ion-progress-bar v-if="uploadInProgress" :value="uploadProgressPercent / 100" type="determinate"></ion-progress-bar>
          <p v-if="uploadInProgress" class="upload-progress-text">{{ uploadProgressPercent }}%</p>
          <p v-if="uploadInProgress" class="upload-warning">⚠️ Не закрывайте окно до завершения загрузки.</p>
          <ion-button expand="block" @click="doUpload()" :disabled="!canUpload || uploadInProgress">
            {{ uploadInProgress ? 'Загрузка…' : 'Загрузить на сервер' }}
          </ion-button>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Видео раздела «{{ mediaCategoryTitle }}»</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p v-if="hasProcessingVideos" class="info-hint">ℹ️ Конвертация видео идёт на сервере. Можно закрыть окно и вернуться позднее — прогресс обновится автоматически.</p>
          <ion-item>
            <ion-select label="Раздел" label-placement="stacked" v-model="filterSectionKey" interface="popover" mode="ios"
              @ionChange="loadVideos()">
              <ion-select-option value="">Все разделы</ion-select-option>
              <ion-select-option v-for="s in educationSections" :key="s.id || s.folderKey" :value="s.folderKey">{{ s.name }}</ion-select-option>
            </ion-select>
          </ion-item>
          <div v-if="videos.length" class="media-file-list">
            <div v-for="v in videos" :key="v.id" class="media-file-row">
              <div class="media-file-main">
                <div class="media-file-title">{{ v.displayName }}</div>
                <div class="media-file-meta">{{ v.sectionName || v.sectionFolderKey }} · {{ v.sourceType }}</div>
                <ion-progress-bar
                  v-if="isProcessingWithProgress(v)"
                  :value="(conversionProgress[Number(v.id)]?.percent ?? 0) / 100"
                  type="determinate"
                  class="conversion-progress"
                ></ion-progress-bar>
              </div>
              <span class="media-status" :class="'media-status--' + effectiveStatus(v)">{{ effectiveStatusLabel(v) }}</span>
              <div class="media-file-actions">
                <button type="button" class="media-icon-btn" @click="renameVideo(v)" title="Переименовать">
                  <ion-icon :icon="pencilOutline"></ion-icon>
                </button>
                <button
                  v-if="v.status === 'PROCESSING'"
                  type="button"
                  class="media-icon-btn"
                  @click="cancelConversion(v)"
                  title="Остановить"
                >
                  <ion-icon :icon="stopCircleOutline"></ion-icon>
                </button>
                <button
                  v-if="v.status === 'FAILED' || v.status === 'CANCELLED'"
                  type="button"
                  class="media-icon-btn"
                  @click="retryConversion(v)"
                  title="Повторить"
                >
                  <ion-icon :icon="refreshOutline"></ion-icon>
                </button>
                <button
                  v-if="v.status === 'READY' || v.status === 'FAILED' || v.status === 'CANCELLED'"
                  type="button"
                  class="media-icon-btn media-icon-btn--danger"
                  @click="deleteVideo(v)"
                  title="Удалить"
                >
                  <ion-icon :icon="trashOutline"></ion-icon>
                </button>
              </div>
            </div>
          </div>
          <p v-else class="empty-hint">Нет видео или загрузка не реализована.</p>
        </ion-card-content>
      </ion-card>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { EducationSection } from '@/model/education-section.model';
import { GlobalContext } from '@/model/globalcontext.model';
import { DEFAULT_MEDIA_CATEGORY, MediaCategoryKey, mediaCategoryLabel, normalizeMediaCategory } from '@/model/media-category.model';
import { IDiskSpace, IEducationVideo, VideoProcessingStatus } from '@/model/video-upload.model';
import AdminService from '@/services/admin.service';
import { subscribeToConversionProgress } from '@/services/video-conversion-progress.service';
import {
  alertController,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonPage,
  IonProgressBar,
  IonSelect,
  IonSelectOption,
  toastController,
} from '@ionic/vue';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { cloudUploadOutline, pencilOutline, refreshOutline, stopCircleOutline, trashOutline } from 'ionicons/icons';
import { defineComponent, inject, ref } from 'vue';

export default defineComponent({
  name: 'AdminVideoUpload',
  components: {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonIcon,
    IonInput,
    IonItem,
    IonPage,
    IonProgressBar,
    IonSelect,
    IonSelectOption,
    PageHeaderWithMenu,
  },
  props: {
    embedMode: { type: Boolean, default: false },
    mediaCategory: { type: String, default: DEFAULT_MEDIA_CATEGORY },
    /** Если задан — загрузка и список только для этого подраздела (навигация родителя). */
    sectionFolderKey: { type: String, default: '' },
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    const fileInput = ref<HTMLInputElement | null>(null);
    return {
      isMobile: globalContext.isMobile,
      fileInput,
      cloudUploadOutline,
      trashOutline,
      stopCircleOutline,
      refreshOutline,
      pencilOutline,
    };
  },
  data() {
    return {
      /** Порог (2 ГБ): выше — показываем предупреждение о возможном сбое загрузки (см. LARGE_FILE_UPLOAD.md). */
      LARGE_FILE_WARN_BYTES: 2 * 1024 * 1024 * 1024,
      adminService: new AdminService(),
      diskSpace: null as IDiskSpace | null,
      educationSections: [] as EducationSection[],
      videos: [] as IEducationVideo[],
      uploadSectionKey: '' as string,
      uploadDisplayName: '',
      selectedFile: null as File | null,
      filterSectionKey: '',
      uploadInProgress: false,
      uploadProgressPercent: 0,
      uploadCancelled: false,
      currentUploadId: null as string | null,
      conversionProgress: {} as Record<number, { percent: number; status: string }>,
      progressUnsubscribers: {} as Record<number, () => void>,
      headerScrolled: false,
      videoDragOver: false,
      videoDragDepth: 0,
    };
  },
  computed: {
    mediaCategoryKey(): MediaCategoryKey {
      return normalizeMediaCategory(this.mediaCategory);
    },
    mediaCategoryTitle(): string {
      return mediaCategoryLabel(this.mediaCategoryKey);
    },
    lockedSectionKey(): string {
      return (this.sectionFolderKey || '').trim();
    },
    lockedSectionTitle(): string {
      const key = this.lockedSectionKey;
      if (!key) return '';
      const found = this.educationSections.find((s) => s.folderKey === key);
      return found?.name || key;
    },
    canUpload(): boolean {
      if (!this.selectedFile || !this.uploadSectionKey || !this.uploadDisplayName?.trim()) return false;
      if (this.diskSpace && this.selectedFile.size > this.diskSpace.availableBytes) return false;
      return true;
    },
    hasProcessingVideos(): boolean {
      return this.videos.some((v) => v.status === 'PROCESSING');
    },
  },
  mounted() {
    this.applyLockedSection();
    this.loadDiskSpace();
    this.loadSections();
    this.loadVideos();
  },
  beforeUnmount() {
    Object.values(this.progressUnsubscribers).forEach((fn) => fn());
    this.progressUnsubscribers = {};
  },
  watch: {
    mediaCategory() {
      if (!this.lockedSectionKey) {
        this.uploadSectionKey = '';
        this.filterSectionKey = '';
      }
      this.loadSections();
      this.loadVideos();
    },
    sectionFolderKey() {
      this.applyLockedSection();
      this.loadVideos();
    },
  },
  methods: {
    applyLockedSection() {
      const key = this.lockedSectionKey;
      if (!key) return;
      this.uploadSectionKey = key;
      this.filterSectionKey = key;
    },
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    formatBytes(bytes: number): string {
      const n = Number(bytes);
      if (!Number.isFinite(n) || n < 0) return '—';
      if (n === 0) return '0 Б';
      const k = 1024;
      const sizes = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ', 'ПБ'];
      const i = Math.min(sizes.length - 1, Math.floor(Math.log(n) / Math.log(k)));
      return `${(n / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
    },
    statusLabel(s: VideoProcessingStatus): string {
      const map: Record<VideoProcessingStatus, string> = {
        UPLOADED: 'Загружено',
        PROCESSING: 'В обработке',
        READY: 'Готово',
        FAILED: 'Ошибка',
        CANCELLED: 'Отменено',
      };
      return map[s] || s;
    },
    pickVideoFile() {
      if (this.uploadInProgress) return;
      const input = (this.fileInput || this.$refs.fileInput) as HTMLInputElement | undefined;
      input?.click();
    },
    isVideoFile(file: File): boolean {
      if (file.type && file.type.startsWith('video/')) return true;
      const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
      return ['mp4', 'mov', 'm4v', 'webm', 'mkv', 'avi'].includes(ext);
    },
    assignVideoFile(file: File | null) {
      if (!file) {
        this.selectedFile = null;
        return;
      }
      if (!this.isVideoFile(file)) {
        this.selectedFile = null;
        void toastController.create({
          message: 'Допустимы только видеофайлы',
          duration: 2500,
          position: 'top',
          color: 'danger',
        }).then((t) => t.present());
        return;
      }
      this.selectedFile = file;
    },
    onFileSelected(ev: Event) {
      const input = ev.target as HTMLInputElement;
      this.assignVideoFile(input.files?.[0] || null);
      if (!this.selectedFile && input) input.value = '';
    },
    onVideoDragEnter(ev: DragEvent) {
      if (this.uploadInProgress) return;
      this.videoDragDepth += 1;
      this.videoDragOver = true;
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
    },
    onVideoDragOver(ev: DragEvent) {
      if (this.uploadInProgress) return;
      this.videoDragOver = true;
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
    },
    onVideoDragLeave() {
      this.videoDragDepth = Math.max(0, this.videoDragDepth - 1);
      if (this.videoDragDepth === 0) this.videoDragOver = false;
    },
    onVideoDrop(ev: DragEvent) {
      this.videoDragDepth = 0;
      this.videoDragOver = false;
      if (this.uploadInProgress) return;
      const file = ev.dataTransfer?.files?.[0] || null;
      this.assignVideoFile(file);
    },
    async loadDiskSpace() {
      try {
        this.diskSpace = await this.adminService.getDiskSpace();
      } catch (e) {
        console.error('loadDiskSpace', e);
      }
    },
    async loadSections() {
      try {
        this.educationSections = await this.adminService.getEducationSections(this.mediaCategoryKey);
        if (this.lockedSectionKey) {
          this.applyLockedSection();
        } else if (this.educationSections.length && !this.uploadSectionKey) {
          this.uploadSectionKey = this.educationSections[0].folderKey;
        }
      } catch (e) {
        console.error('loadSections', e);
      }
    },
    effectiveStatus(v: IEducationVideo): VideoProcessingStatus {
      const prog = this.conversionProgress[Number(v.id)];
      if (prog?.status === 'READY' || prog?.status === 'FAILED') return prog.status as VideoProcessingStatus;
      return v.status;
    },
    effectiveStatusLabel(v: IEducationVideo): string {
      return this.statusLabel(this.effectiveStatus(v));
    },
    isProcessingWithProgress(v: IEducationVideo): boolean {
      const videoId = Number(v.id);
      return v.status === 'PROCESSING' && this.conversionProgress[videoId] != null;
    },
    async loadVideos() {
      try {
        if (this.embedMode && !this.lockedSectionKey) {
          this.videos = [];
          return;
        }
        const prev = this.videos;
        const sectionFilter = this.lockedSectionKey || this.filterSectionKey || undefined;
        this.videos = await this.adminService.getEducationVideos(
          sectionFilter,
          this.mediaCategoryKey
        );
        const processingIds = new Set((this.videos as IEducationVideo[]).filter((v) => v.status === 'PROCESSING').map((v) => Number(v.id)));
        const prevProcessingIds = new Set((prev || []).filter((v) => v.status === 'PROCESSING').map((v) => Number(v.id)));
        prevProcessingIds.forEach((id) => {
          if (!processingIds.has(id)) {
            const unsub = this.progressUnsubscribers[id];
            if (unsub) unsub();
            delete this.progressUnsubscribers[id];
          }
        });
        processingIds.forEach((videoId) => {
          if (this.progressUnsubscribers[videoId]) return;
          const unsub = subscribeToConversionProgress(videoId, (data) => {
            const id = Number(data.videoId);
            this.conversionProgress = {
              ...this.conversionProgress,
              [id]: { percent: data.percent, status: data.status },
            };
            if (data.status === 'READY' || data.status === 'FAILED') {
              this.loadVideos();
            }
          });
          this.progressUnsubscribers[videoId] = unsub;
        });
      } catch (e) {
        console.error('loadVideos', e);
      }
    },
    async doUpload() {
      if (!this.canUpload || !this.selectedFile) return;
      this.uploadInProgress = true;
      this.uploadProgressPercent = 0;
      this.uploadCancelled = false;
      this.currentUploadId = null;
      try {
        await this.adminService.uploadEducationVideoWithCallback(
          this.selectedFile,
          this.uploadSectionKey,
          this.uploadDisplayName.trim(),
          (uploadedBytes: number, totalBytes: number) => {
            this.uploadProgressPercent = totalBytes ? Math.round((100 * uploadedBytes) / totalBytes) : 0;
          },
          (uploadId: string) => {
            this.currentUploadId = uploadId;
          },
          () => this.uploadCancelled,
          this.mediaCategoryKey
        );
        const toast = await toastController.create({
          message: 'Видео загружено. Статус: В обработке.',
          duration: 3000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.uploadDisplayName = '';
        this.selectedFile = null;
        if (this.fileInput) this.fileInput.value = '';
        this.loadDiskSpace();
        this.loadVideos();
      } catch (e: unknown) {
        if (this.uploadCancelled) {
          const toast = await toastController.create({
            message: 'Загрузка отменена.',
            duration: 2000,
            position: 'top',
            color: 'medium',
          });
          await toast.present();
        } else {
          console.error('doUpload', e);
          const err = e as Error & { status?: number; response?: { status?: number } };
          const status = err.status ?? err.response?.status;
          const message = status === 400
            ? 'Ошибка загрузки: проверьте файл и размер (на сервере лимит 5 ГБ; nginx — client_max_body_size)'
            : status === 413
            ? 'Файл больше, чем разрешает прокси или сервер. Для nginx укажите client_max_body_size (например 5G) в server или location с API.'
            : 'Ошибка загрузки видео';
          const toast = await toastController.create({
            message,
            duration: 4000,
            position: 'top',
            color: 'danger',
          });
          await toast.present();
        }
      } finally {
        this.uploadInProgress = false;
        this.uploadProgressPercent = 0;
        this.currentUploadId = null;
      }
    },
    async cancelUpload() {
      if (!this.currentUploadId) return;
      this.uploadCancelled = true;
      try {
        await this.adminService.abortChunkedUpload(this.currentUploadId);
        this.adminService.clearResumableUpload(this.currentUploadId);
      } catch (e) {
        console.error('cancelUpload', e);
      }
    },
    async renameVideo(video: IEducationVideo) {
      const alert = await alertController.create({
        header: 'Переименовать видео',
        cssClass: 'alert-dark-theme',
        inputs: [
          {
            name: 'displayName',
            type: 'text',
            value: video.displayName,
            placeholder: 'Новое название',
          },
        ],
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Сохранить',
            handler: async (data) => {
              const newName = (data.displayName || '').trim();
              if (!newName) {
                const toast = await toastController.create({
                  message: 'Название не может быть пустым.',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
                return false;
              }
              if (newName === video.displayName) {
                return true;
              }
              try {
                await this.adminService.updateEducationVideo(video.id, { displayName: newName });
                const toast = await toastController.create({
                  message: 'Название обновлено.',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
                this.loadVideos();
              } catch (e) {
                console.error('renameVideo', e);
                const toast = await toastController.create({
                  message: 'Ошибка при переименовании.',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
              return true;
            },
          },
        ],
      });
      await alert.present();
    },
    async deleteVideo(video: IEducationVideo) {
      const alert = await alertController.create({
        header: 'Удалить видео?',
        message: `Удалить «${video.displayName}»? Файлы на сервере будут удалены.`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                await this.adminService.deleteEducationVideo(video.id);
                const toast = await toastController.create({
                  message: 'Видео удалено.',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
                this.loadVideos();
                this.loadDiskSpace();
              } catch (e) {
                console.error('deleteVideo', e);
                const toast = await toastController.create({
                  message: 'Ошибка удаления видео.',
                  duration: 3000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
            },
          },
        ],
      });
      await alert.present();
    },
    async cancelConversion(video: IEducationVideo) {
      const alert = await alertController.create({
        header: 'Отменить конвертацию?',
        message: `Остановить конвертацию «${video.displayName}»? Частично сконвертированный файл будет удалён.`,
        buttons: [
          { text: 'Нет', role: 'cancel' },
          {
            text: 'Отменить конвертацию',
            role: 'destructive',
            handler: async () => {
              try {
                await this.adminService.cancelConversion(video.id);
                const toast = await toastController.create({
                  message: 'Конвертация отменена.',
                  duration: 2000,
                  position: 'top',
                  color: 'warning',
                });
                await toast.present();
                this.loadVideos();
              } catch (e) {
                console.error('cancelConversion', e);
                const toast = await toastController.create({
                  message: 'Ошибка отмены конвертации.',
                  duration: 3000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
            },
          },
        ],
      });
      await alert.present();
    },
    async retryConversion(video: IEducationVideo) {
      try {
        await this.adminService.retryConversion(video.id);
        const toast = await toastController.create({
          message: 'Конвертация перезапущена.',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.loadVideos();
      } catch (e) {
        console.error('retryConversion', e);
        const errStatus = (e as { response?: { status?: number } })?.response?.status;
        let message = 'Ошибка перезапуска конвертации.';
        if (errStatus === 422) {
          message = 'Исходный файл (.upload) не найден. Перезапуск невозможен.';
        }
        const toast = await toastController.create({
          message,
          duration: 4000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  --background: var(--liga-float-glass-bg-strong);
}

.admin-card ion-card-title {
  color: var(--liga-chrome-text);
}

.admin-card ion-card-content {
  color: var(--liga-chrome-text);
}

.admin-card ion-item,
.admin-card ion-list,
.admin-card ion-list ion-item {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
  --color: var(--liga-chrome-text);
}
.admin-card ion-list {
  background: transparent !important;
}
.admin-card ion-list ion-item::part(native),
.admin-card ion-item::part(native) {
  --background: transparent !important;
  background: transparent !important;
}

.disk-card p {
  margin: 0;
}

.file-too-big {
  color: var(--ion-color-danger);
}

.upload-progress-text {
  margin: 4px 0 0;
  font-size: 0.9em;
  color: var(--liga-chrome-text);
}

.upload-warning {
  margin: 4px 0 0;
  font-size: 0.9em;
  color: var(--ion-color-warning);
  font-weight: 600;
}

.info-hint {
  margin: 0 0 12px;
  font-size: 0.9em;
  color: var(--ion-color-primary);
}

.media-file-list {
  display: flex;
  flex-direction: column;
}

.media-file-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  padding: 10px 2px;
  border-bottom: 1px solid var(--liga-float-glass-border, rgba(255, 255, 255, 0.1));
}

.media-file-row:last-child {
  border-bottom: none;
}

.media-file-main {
  flex: 1;
  min-width: 0;
}

.media-file-title {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--liga-chrome-text);
  word-break: break-word;
  line-height: 1.3;
}

.media-file-meta {
  margin-top: 2px;
  font-size: 0.75rem;
  color: var(--liga-chrome-text-muted);
}

.conversion-progress {
  margin-top: 6px;
  height: 3px;
}

.media-status {
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 3px 9px;
  border-radius: 999px;
  line-height: 1.2;
  white-space: nowrap;
}

.media-status--READY {
  color: #1b7a36;
  background: rgba(46, 160, 67, 0.16);
}

.media-status--PROCESSING,
.media-status--UPLOADED {
  color: #9a6700;
  background: rgba(234, 179, 8, 0.18);
}

.media-status--FAILED {
  color: #b42318;
  background: rgba(239, 68, 68, 0.14);
}

.media-status--CANCELLED {
  color: var(--liga-chrome-text-muted);
  background: rgba(127, 127, 127, 0.14);
}

.media-file-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.media-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--liga-chrome-text-muted);
  cursor: pointer;
  padding: 0;
}

.media-icon-btn:hover {
  background: rgba(127, 127, 127, 0.12);
  color: var(--liga-chrome-text);
}

.media-icon-btn--danger:hover {
  background: rgba(239, 68, 68, 0.12);
  color: var(--ion-color-danger);
}

.media-icon-btn ion-icon {
  font-size: 1.05rem;
}

.empty-hint {
  color: var(--liga-chrome-text-muted);
}

.embed-pick-hint {
  margin: 8px 0;
}

.admin-video-embed {
  padding: 0 0 24px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.admin-video-embed .admin-card {
  margin: 12px 0 0;
}

.file-input-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.drop-zone {
  margin: 10px 0 4px;
  padding: 22px 16px;
  border: 1.5px dashed rgba(232, 93, 4, 0.45);
  border-radius: 14px;
  background: rgba(232, 93, 4, 0.04);
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  user-select: none;
}

.drop-zone:hover {
  border-color: var(--ion-color-orange);
  background: rgba(232, 93, 4, 0.08);
}

.drop-zone--active {
  border-color: var(--ion-color-orange);
  border-style: solid;
  background: rgba(232, 93, 4, 0.14);
}

.drop-zone--has-file {
  border-style: solid;
  border-color: rgba(232, 93, 4, 0.55);
}

.drop-zone--disabled {
  opacity: 0.55;
  pointer-events: none;
}

.drop-zone-icon {
  font-size: 1.75rem;
  color: var(--ion-color-orange);
  margin-bottom: 6px;
}

.drop-zone-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--liga-chrome-text);
  word-break: break-word;
}

.drop-zone-hint {
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: var(--liga-chrome-text-muted);
}

.header-title {
  letter-spacing: 0.08em;
}

/* Кнопки в стиле приложения: чёрно-оранжевый */
.admin-card ion-button:not([fill='clear']):not([fill='outline']) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: var(--liga-on-accent);
  --color-activated: var(--liga-on-accent);
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-card ion-button[fill='clear'] {
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  font-weight: 600;
}
</style>
